/**
 * What a user reads when a capability refused the click.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * 🔴 THIS IS THE ONLY COPY FOR A CAPABILITY OUTAGE, AND IT IS DELIBERATELY NOT
 *    BESIDE THE ERROR HANDLER THAT CONSUMES IT.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `CAPABILITY_UNAVAILABLE` crosses the wire carrying a machine name and nothing
 * human-readable — the backend's `message` is the code string again, pinned by
 * `z.literal`, precisely so the sentence can change without a contract change.
 * So the sentence is this app's, and it lives beside `CapabilityGuard`, which
 * owns the BEFORE-the-click half of the same question.
 *
 * It is NOT in `src/i18n/errors/`: that catalog's own
 * `types.ts` declares itself a mirror of the backend error contract, and a
 * sentence the backend does not carry has no business in a mirror of it.
 *
 * ── TRANSLATED, WHERE THE FOUR CONSTANTS IN `capability.ts` ARE NOT ──────────
 *
 * `WITHHELD_REASON`, `UNKNOWN_TITLE`, `UNKNOWN_REASON` and every `title` a
 * `CapabilityGuard` call site passes are plain English literals. This module
 * breaks with them on purpose rather than by oversight: an outage sentence is
 * what a non-English enterprise tenant reads on a bad day, and it reaches them
 * through a toast on every surface that has not wired a dead region. Those four
 * are a migration this module does not perform; `en` and `fr` are the locales
 * this app actually ships copy in (2,220 and 2,087 keys against ~168 in the
 * other four), so the other four fall back to English exactly as they do for
 * every other translated surface here.
 *
 * ── WHY EVERY SENTENCE REFUSES "TRY AGAIN" ──────────────────────────────────
 *
 * A capability is off because a requirement it needs is unmet on the SERVER.
 * Waiting cannot satisfy it, and neither can an organization admin — unlike
 * `FEATURE_ACCESS_RESTRICTED`, there is nobody in the customer's org to ask.
 * `service_unavailable` in the shared catalog reads "please try again in a few
 * moments", and NEX-3949 measured what advice that cannot come true costs:
 * 9,344 events from 22 users retrying a dead end. So the body states that the
 * condition is deployment-wide, that nothing on the user's side caused it, and
 * that the people who can act have already been told — which is a fact, not a
 * courtesy: `apps/backend/src/_common/capabilities/alerting/` ships a Sentry
 * sink and a Slack sink beside `report-degraded-capabilities.service.ts`, and
 * `log-capabilities-on-boot.service.ts` names every non-ready row at boot.
 *
 * @see CapabilityGuard.tsx — the before-the-click half, and its own four states
 * @see ../utils/error-handler.ts — `getCapabilityOutage`, which resolves the name
 */

import type { TFunction } from "i18next"

import type { OutageCapabilityName } from "@/common/utils/error-handler"

/**
 * The feature name a user sees, per capability this build can name.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * 🔴 EXHAUSTIVE OVER THE CLIENT UNION, WHICH IS THE WHOLE POINT OF THE `Record`.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * A registry name is a WIRE value — `spreadsheet-import`, `support-chat-identity`
 * — and putting one on a customer's screen is the failure this table exists to
 * make impossible. A `Record` keyed on the union means adding a member to
 * {@link OutageCapabilityName} without a label here is a compile error, where a
 * partial map or a `?? capability` fallback would render the raw name and pass
 * every check. `pnpm typecheck` is the gate; `capability-outage-copy.test.ts`
 * scores that it fires.
 *
 * ⚠️ The union is this BUILD's census of the server's registry, not the registry.
 * A route gated with a name shipped after this bundle resolves to `null` through
 * `getCapabilityOutage`, and the unnamed title below is what it gets — which is
 * correct, and is why no arm here needs a fallback.
 */
export const CAPABILITY_LABEL: Record<OutageCapabilityName, string> = {
  "spreadsheet-import": "common.capability.label.spreadsheet-import",
  "document-generation": "common.capability.label.document-generation",
  "support-chat-identity": "common.capability.label.support-chat-identity"
}

/** The two sentences a capability outage renders, already translated. */
export interface CapabilityOutageCopy {
  /** Names the blockage — the feature when this build can name it. */
  readonly title: string
  /** Why it happened and who acts. Never asks the user to retry. */
  readonly body: string
}

/**
 * Resolves an outage's sentences for the active language.
 *
 * Pure, and takes `t` as a parameter rather than calling a hook — the app's own
 * idiom for copy resolved outside a component (`runMomentum.ts`, `copy-text.ts`,
 * `quantityPhrase.ts` and five more take a `TFunction` the same way). That is
 * what lets `classifyError`, which is documented pure and SSR-safe, reach the
 * same sentence a React surface renders.
 *
 * @param capability the name from the wire, or `null` when this build cannot name it
 */
export function capabilityOutageCopy(
  capability: OutageCapabilityName | null,
  t: TFunction
): CapabilityOutageCopy {
  return {
    title:
      capability === null
        ? t("common.capability.outage.title-unnamed")
        : t("common.capability.outage.title", { feature: t(CAPABILITY_LABEL[capability]) }),
    body: t("common.capability.outage.body")
  }
}
